import React from 'react';
import type { MergedSiteContent } from '@/context/SiteContentContext';
import type { ProTemplate } from '@/config/templates';

interface LocalBusinessJsonLdProps {
  content: MergedSiteContent;
}

export const LocalBusinessJsonLd: React.FC<LocalBusinessJsonLdProps> = ({ content }) => {
  const { template, serviceAreas, seoCanonical } = content;
  const contact = template.contact;
  const stats = template.stats;
  const brand = template.brand;

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": seoCanonical || undefined,
    name: brand?.name || '',
    description: content.seoDescription || '',
    telephone: contact?.phone || '',
    email: contact?.email || '',
    url: seoCanonical || '',
    image: content.heroImageUrl || undefined,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: contact?.address || '',
      addressLocality: contact?.city || '',
      addressRegion: contact?.state || '',
      postalCode: contact?.zip || '',
      addressCountry: "US",
    },
    aggregateRating: stats?.googleRating ? {
      "@type": "AggregateRating",
      ratingValue: stats.googleRating,
      reviewCount: stats.reviewCount || 0,
      bestRating: 5,
      worstRating: 1,
    } : undefined,
    areaServed: serviceAreas.map((area) => ({
      "@type": "City",
      name: typeof area === 'string' ? area : area.name,
    })),
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
};

interface FAQJsonLdProps {
  faqs: Array<{ question: string; answer: string }>;
}

export const FAQJsonLd: React.FC<FAQJsonLdProps> = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
};

interface ServiceJsonLdProps {
  service: { title: string; description: string; slug: string };
  template: ProTemplate;
  baseUrl?: string;
}

export const ServiceJsonLd: React.FC<ServiceJsonLdProps> = ({ service, template, baseUrl = '' }) => {
  const brand = template.brand;
  const contact = template.contact;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.description,
    url: `${baseUrl}/services#${service.slug}`,
    provider: {
      "@type": "LocalBusiness",
      name: brand?.name || '',
      telephone: contact?.phone || '',
    },
    areaServed: {
      "@type": "State",
      name: contact?.state || '',
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
};

interface BreadcrumbJsonLdProps {
  items: Array<{ name: string; url: string }>;
}

export const BreadcrumbJsonLd: React.FC<BreadcrumbJsonLdProps> = ({ items }) => {
  if (!items || items.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
};

interface WebSiteJsonLdProps {
  name: string;
  url: string;
  description?: string;
}

export const WebSiteJsonLd: React.FC<WebSiteJsonLdProps> = ({ name, url, description }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    url,
    description,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${url}/services?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
};

interface OrganizationJsonLdProps {
  template: ProTemplate;
  logoUrl?: string;
}

export const OrganizationJsonLd: React.FC<OrganizationJsonLdProps> = ({ template, logoUrl }) => {
  const brand = template.brand;
  const contact = template.contact;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brand?.name || '',
    logo: logoUrl,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: contact?.phone || '',
      contactType: "customer service",
      availableLanguage: "English",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
};

interface ArticleJsonLdProps {
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  authorName?: string;
  publishedAt?: string;
  updatedAt?: string;
  wordCount?: number;
  category?: string;
  publisherName?: string;
  publisherLogoUrl?: string;
}

export const ArticleJsonLd: React.FC<ArticleJsonLdProps> = ({
  title, description, url, imageUrl, authorName, publishedAt, updatedAt,
  wordCount, category, publisherName, publisherLogoUrl,
}) => {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url,
    image: imageUrl || undefined,
    wordCount: wordCount || undefined,
    articleSection: category || undefined,
    datePublished: publishedAt || undefined,
    dateModified: updatedAt || publishedAt || undefined,
    author: {
      "@type": authorName ? "Person" : "Organization",
      name: authorName || publisherName || '',
    },
    publisher: publisherName ? {
      "@type": "Organization",
      name: publisherName,
      logo: publisherLogoUrl ? {
        "@type": "ImageObject",
        url: publisherLogoUrl,
      } : undefined,
    } : undefined,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
};
